import * as THREE from 'three';
import { hexToWorld, type BattleState, type DungeonLayout } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';

/**
 * The race (bet 8, docs/SAGAS.md): Hilde's hourglass, as tall as a man, stood in the crypt's first room on the side away
 * from the way down. Iron frame with her red mark, two glass bulbs, and the sand running from the top one to the bottom
 * as the race's clock runs (`rules.race.limit` after the start); in the last half minute the sand glows red, and with
 * the clock out it stops, all of it below.
 *
 * Draw calls: the frame (one merged mesh), the glass (one), the two heaps of sand and the stream (three small meshes),
 * a glow layer. No lights.
 */

const IRON = '#2f3137';
const IRON_EDGE = '#5a5e68';
const WOOD = '#4a3a30';
const ENAMEL = '#b8402c';
const SAND = new THREE.Color('#d8d2c4');
const SAND_LATE = new THREE.Color('#ff6a4a');
const RED = new THREE.Color('#ff4f3a');
const COOL = new THREE.Color('#cfd8e6');
/** How tall it stands, and the bulbs' size (world units). */
const TALL = 1.95;
const BULB = 0.4;
/** The last half minute, in ticks. */
const LATE = 30 * 12;

export class RaceLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(8);
  private top: THREE.Mesh;
  private bottom: THREE.Mesh;
  private stream: THREE.Mesh;
  private sandMat: THREE.MeshStandardMaterial;
  private p: THREE.Vector3;
  private now = 0;

  constructor(
    ctx: OmenCtx,
    layout: DungeonLayout,
    private limit: number,
  ) {
    this.group.name = 'race-hourglass';
    const room = layout.rooms[0];
    const next = layout.rooms[1] ?? room;
    const c = room ? hexToWorld(room.q, room.r) : { x: 0, y: 0 };
    const n = next ? hexToWorld(next.q, next.r) : { x: c.x + 1000, y: c.y };
    // Away from the way on, a hex and a half from the room's middle.
    const dx = c.x - n.x;
    const dy = c.y - n.y;
    const len = Math.hypot(dx, dy) || 1;
    const off = 1500;
    const at = { x: c.x + (dx / len) * off, y: c.y + (dy / len) * off };
    this.p = ctx.at(at.x, at.y, 0);
    const { x, z } = this.p;
    const g = ctx.ground(x, z);
    const yaw = Math.atan2(-dx, -dy);

    const kit = new Kit();
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 8);
    const s = WORLD_PER_HEX;
    const k = s / 1.15;
    // Base: a thick board bound in iron. Cap: an open iron ring (from above the glass and its sand show through).
    kit.add(box, WOOD, place(x, g + 0.06 * k, z, yaw, new THREE.Vector3(0.78 * k, 0.12 * k, 0.78 * k)));
    kit.add(box, IRON_EDGE, place(x, g + 0.06 * k, z, yaw, new THREE.Vector3(0.82 * k, 0.04 * k, 0.82 * k)));
    for (const [a, b, w, d] of [[0, -1, 0.78, 0.08], [0, 1, 0.78, 0.08], [-1, 0, 0.08, 0.78], [1, 0, 0.08, 0.78]] as const) {
      const lx = a * 0.35 * k;
      const lz = b * 0.35 * k;
      kit.add(box, IRON, place(x + lx * Math.cos(yaw) + lz * Math.sin(yaw), g + (TALL - 0.04) * k, z - lx * Math.sin(yaw) + lz * Math.cos(yaw), yaw, new THREE.Vector3(w * k, 0.07 * k, d * k)));
    }
    // Four iron posts.
    const cos = Math.cos(yaw);
    const sin = Math.sin(yaw);
    for (const [a, b] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
      const lx = a * 0.35 * k;
      const lz = b * 0.35 * k;
      kit.add(cyl, IRON, place(x + lx * cos + lz * sin, g + (TALL / 2) * k, z - lx * sin + lz * cos, 0, new THREE.Vector3(0.06 * k, (TALL - 0.1) * k, 0.06 * k)));
    }
    // Her mark on the cap's front: a hammer over an anvil, in red enamel.
    const fx = Math.sin(yaw) * 0.42 * k;
    const fz = Math.cos(yaw) * 0.42 * k;
    kit.add(box, ENAMEL, place(x + fx, g + 0.08 * k, z + fz, yaw, new THREE.Vector3(0.3 * k, 0.04 * k, 0.02 * k)));
    kit.add(box, ENAMEL, place(x + fx, g + 0.13 * k, z + fz, yaw, new THREE.Vector3(0.06 * k, 0.08 * k, 0.025 * k)));
    const frameMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.65, metalness: 0.3, flatShading: true });
    this.mats.push(frameMat);
    const frame = kit.build(frameMat, 'race-frame');
    if (frame) {
      this.geos.push(frame.geometry);
      this.group.add(frame);
    }
    box.dispose();
    cyl.dispose();

    // The glass: two bulbs meeting at a narrow neck.
    const mid = g + (TALL / 2) * k;
    const bulb = new THREE.SphereGeometry(BULB * k, 16, 12);
    bulb.scale(1, 1.25, 1);
    const glassMat = new THREE.MeshStandardMaterial({ color: COOL, transparent: true, opacity: 0.28, roughness: 0.1, metalness: 0.1, depthWrite: false, emissive: COOL, emissiveIntensity: 0.12 });
    this.mats.push(glassMat);
    this.geos.push(bulb);
    for (const sgn of [1, -1]) {
      const m = new THREE.Mesh(bulb, glassMat);
      m.position.set(x, mid + sgn * BULB * 1.15 * k, z);
      m.renderOrder = 3;
      this.group.add(m);
    }
    // The sand: a cone in each bulb (scaled by what is left and what fell) and the thin stream between.
    this.sandMat = new THREE.MeshStandardMaterial({ color: SAND.clone(), roughness: 0.9, emissive: SAND.clone(), emissiveIntensity: 0.25 });
    this.mats.push(this.sandMat);
    // Top: a funnel (point down) so it drains from the middle; bottom: a heap (point up).
    const funnel = new THREE.ConeGeometry(BULB * 0.9 * k, BULB * 1.2 * k, 16).rotateX(Math.PI).translate(0, BULB * 0.6 * k, 0);
    const heap = new THREE.ConeGeometry(BULB * 0.95 * k, BULB * 1.1 * k, 16).translate(0, BULB * 0.55 * k, 0);
    const thread = new THREE.CylinderGeometry(0.012 * k, 0.012 * k, BULB * 2.2 * k, 5).translate(0, -BULB * 1.1 * k, 0);
    this.geos.push(funnel, heap, thread);
    this.top = new THREE.Mesh(funnel, this.sandMat);
    this.top.position.set(x, mid + 0.04 * k, z);
    this.bottom = new THREE.Mesh(heap, this.sandMat);
    this.bottom.position.set(x, mid - BULB * 2.35 * k, z);
    this.stream = new THREE.Mesh(thread, this.sandMat);
    this.stream.position.set(x, mid, z);
    this.group.add(this.top, this.bottom, this.stream);
    this.group.add(this.glow.mesh);
    this.draw(null);
    this.group.updateMatrixWorld(true);
  }

  /** Every redraw: the sand left above and fallen below, running while the race runs; red near the end. */
  draw(s: BattleState | null): void {
    const spent = s?.started ? Math.min(s.waveT, this.limit) : 0;
    const f = this.limit > 0 ? spent / this.limit : 0;
    const left = 1 - f;
    // A cone's volume goes with its height cubed: scale so the heaps look like the share.
    const up = Math.cbrt(Math.max(0.0001, left));
    const down = Math.cbrt(Math.max(0.0001, f));
    this.top.scale.set(up, up, up);
    this.top.visible = left > 0.002;
    this.bottom.scale.set(down, down, down);
    this.bottom.visible = f > 0.002;
    const running = !!s?.started && s.result === 'running' && spent < this.limit;
    this.stream.visible = running;
    const late = running && this.limit - spent <= LATE;
    const pulse = 0.5 + 0.5 * Math.sin(this.now * 6);
    this.sandMat.color.copy(SAND).lerp(SAND_LATE, late ? 0.7 : 0);
    this.sandMat.emissive.copy(late ? RED : SAND);
    this.sandMat.emissiveIntensity = late ? 0.35 + 0.35 * pulse : 0.25;
    this.top.updateMatrix();
    this.bottom.updateMatrix();
    this.glow.begin();
    if (late) this.glow.add(this.p.x, this.p.y + (TALL / 2) * (WORLD_PER_HEX / 1.15), this.p.z, RED, 1.2, 0.35 + 0.25 * pulse);
    this.glow.end();
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
